import React from "react";
import Label from "@/components/elements/Label";
import Dropdown from "@/components/elements/Dropdown";
import TextInput from "@/components/elements/TextInput";
import AutoComplete from "@/components/elements/AutoComplete";

/**
 * Question component that dynamically renders form fields based on configuration
 * Supports multiple field types and conditional field visibility
 * Handles complex field validation and property updates
 *
 * @param {Object} props - Component props
 * @param {Function} props.updateQuestionProperties - Callback for question-specific property updates
 * @param {Object} props.question - Question configuration object with name and fields array
 * @param {Function} props.updateQuestion - Callback for updating question state
 * @param {Function} props.updateProperties - Callback for updating form properties
 * @param {Object} props.properties - Current form properties/values
 * @param {Array} props.errors - Array of validation errors (default: [])
 */
const Question = ({
    updateQuestionProperties,
    question,
    updateQuestion,
    updateProperties,
    properties,
    errors = [],
}) => {
    /**
     * Render the appropriate input component based on field type
     * Supports dropdown, autocomplete, text, and fallback rendering
     *
     * @param {Object} field - Field configuration object
     * @returns {JSX.Element} The rendered form element
     */
    function renderElement(field) {
        switch (field.type) {
            case "dropdown":
                return (
                    <Dropdown
                        errors={errors}
                        properties={properties}
                        field={field}
                        property={field.property}
                        updateProperties={updateProperties}
                        options={field.options}
                        updateQuestionProperties={updateQuestionProperties}
                    />
                );
            case "autocomplete":
                return (
                    <AutoComplete
                        properties={properties}
                        field={field}
                        property={field.property}
                        updateProperties={updateQuestion}
                        options={field.options}
                    />
                );
            case "text":
                return (
                    <TextInput
						error={errors.includes(field.property)}
                        properties={properties}
                        field={field}
                        property={field.property}
                        updateProperties={updateProperties}
                    />
                );
            default:
                // Fallback for unknown field types
                return "foo";
        }
    }

    /**
     * Determine if a field should be displayed based on its conditions
     * Supports array-based conditions and numeric comparisons with operators
     *
     * @param {Object} field - Field configuration object
     * @returns {boolean} Whether the field should be shown
     */
    function showField(field) {
        let show = true;

        // Check if field has conditional display rules
        if (field.hasOwnProperty("condition")) {
            field.condition.forEach((condition) => {

                // Handle array-based conditions (value must be in array)
                if (
                    Array.isArray(condition.condition_value) &&
                    !condition.condition_value.includes(
                        properties[condition.condition_property],
                    )
                ) {
                    show = false;
                    return;

                } else if (Number.isInteger(condition.condition_value)) { // Handle numeric conditions with optional operators

                    if (condition.hasOwnProperty("condition_operator")) {
                        // Use eval for dynamic operator evaluation (e.g., >, <, >=, <=)
                        if (
                            eval(
                                properties[condition.condition_property] +
                                    condition.condition_operator +
                                    condition.condition_value,
                            )
                        ) {
                            show = false;
                            return;
                        }
                    } else {
                        // Default to equality check
                        if (
                            condition.condition_value !==
                            properties[condition.condition_property]
                        ) {
                            show = false;
                            return;
                        }
                    }
                }
            });
        }
        return show;
    }

    return (
        <div className="question">
            {question.name ? <Label label={question.name} /> : <></>}
            {question.fields.map((field, key) => {
                return (
                    showField(field) && (
                        <div className="d-block" key={key}>
                            {field.label ? (
                                <Label label={field.label} />
                            ) : (
                                <></>
                            )}
                            {renderElement(field)}
                        </div>
                    )
                );
            })}
        </div>
    );
};

export default Question;
